import { clamp } from '../utils/number';
import { SankeyGraph, SankeyNode, SankeyNodeMove } from './types';

export const getNodeMovesMap = (
  moves: string | null | undefined
): Map<string, SankeyNodeMove> => new Map(JSON.parse(moves || '[]'));

// Used to track the user's repositioning of specific nodes in the Sankey diagram
class NodeMoves {
  movesMap: Map<string, SankeyNodeMove>;
  sankeyForm: SankeyForm;

  constructor(sankeyForm: SankeyForm) {
    // Initialize the movesMap from the remembered_node_moves field in the Sankey form
    this.movesMap = getNodeMovesMap(
      sankeyForm.getValues('remembered_node_moves')
    );
    this.sankeyForm = sankeyForm;
  }

  // Set the new starting point of any constrained move
  updateLastPosition(node: SankeyNode) {
    node.lastPos = { x: node.x, y: node.y };
  }

  renameNode(oldName: string, newName: string) {
    const oldMove = this.movesMap.get(oldName);
    if (oldMove) {
      this.movesMap.set(newName, oldMove);
      this.movesMap.delete(oldName);
      this.sankeyForm.setValue(
        'remembered_node_moves',
        JSON.stringify(Array.from(this.movesMap.entries())),
        { shouldDirty: true }
      );
    }
  }

  // Save a move so it can be re-applied in the future.
  // The value saved is the % of the available size that the node was moved, not the
  // literal pixel move. This helps when the user is changing spacing or canvas size.
  saveNodeMove(node: SankeyNode) {
    // Always update lastPos when remembering moves
    this.updateLastPosition(node);
    if (node.move[0] === 0 && node.move[1] === 0) {
      // There's no actual move now. If one was stored, forget it.
      this.movesMap.delete(node.name);
    } else {
      // We save moves keyed to their NAME (not their index), so they
      // can be remembered even when the inputs change their order.
      // In the case of a move already remembered, this will replace the
      // original moves with an identical copy.
      this.movesMap.set(node.name, node.move);
    }
    // Serialize the map and store it in the form
    this.sankeyForm.setValue(
      'remembered_node_moves',
      JSON.stringify(Array.from(this.movesMap.entries())),
      { shouldDirty: true }
    );
  }

  // Given a node, store the move in the node object and apply it to its coordinates
  moveNodeTo(node: SankeyNode, move: SankeyNodeMove, graph: SankeyGraph) {
    node.move = move;

    const availableW = graph.w - node.dx;
    const availableH = graph.h - node.dy;

    // Apply the move to the node (halting at the edges of the graph):
    node.x = clamp(node.origPos.x + availableW * move[0], 0, availableW);
    node.y = clamp(node.origPos.y + availableH * move[1], 0, availableH);
  }

  // Applies all the remembered moves to the nodes in the graph and cleans up removed nodes' moves
  applyAndCleanUp(nodes: SankeyNode[], graph: SankeyGraph) {
    // Make a copy of the list of moved node names (so we can destroy it)
    const movedNodes = new Set(this.movesMap.keys());

    // Look for all node objects matching a name in the list and
    // apply the remembered move to them, deleting the name from the list.
    nodes.forEach((node) => {
      const move = this.movesMap.get(node.name);
      if (move) {
        this.moveNodeTo(node, move, graph);
        this.updateLastPosition(node);
        movedNodes.delete(node.name);
      }
    });

    // Any remaining items in movedNodes must refer to Nodes which are no
    // longer with us. Delete those from memory.
    movedNodes.forEach((nodeName) => {
      this.movesMap.delete(nodeName);
    });

    return this;
  }
}

export default NodeMoves;
